export type SourceRegistryStatus = "implemented" | "ready-schema" | "requires-verification" | "optional" | "blocked";
export type SourceCollectionOwner = "SDP" | "FREM_LOCAL_MANUAL" | "UNASSIGNED";
export type FremTransport = "SDP_CONSUMER_API" | "PROJECT_LOCAL_STATIC" | "NOT_CONNECTED";
export type FremProviderCredentialPolicy = "FORBIDDEN" | "NOT_APPLICABLE";

export interface SourceRegistryItem {
  id: string;
  layer: string;
  provider: string;
  dataset: string;
  targetSeries: string[];
  acquisition: "api" | "download" | "crawler" | "manual-review";
  /** Upstream/provider auth used by the collection owner; never a FREM runtime credential requirement. */
  auth: "none" | "api-key" | "unknown";
  collectionOwner: SourceCollectionOwner;
  fremTransport: FremTransport;
  providerCredentialInFrem: FremProviderCredentialPolicy;
  status: SourceRegistryStatus;
  userAction?: string;
}

export const sourceStatusLabels: Record<SourceRegistryStatus, string> = {
  implemented: "연결 완료",
  "ready-schema": "구조 준비",
  "requires-verification": "후속 연결 필요",
  optional: "선택 사항",
  blocked: "연결 차단"
};

export const sourceRegistry: SourceRegistryItem[] = [
  {
    id: "bok-ecos-core",
    layer: "policy/market/funding/credit/real_economy/real_estate/fx",
    provider: "한국은행 ECOS",
    dataset: "722Y001 / 817Y002 / 121Y002 / 121Y013 / 121Y006 / 731Y001 / 513Y001 / 901Y033 / 901Y074",
    targetSeries: [
      "기준금리",
      "국고채 1/2/3/5/10Y",
      "회사채 AA-/BBB-",
      "CD91/KORIBOR12M",
      "금융채 신규/잔액금리",
      "기업·가계·주담대 신규대출금리",
      "USD/KRW",
      "경제심리지수",
      "전산업생산지수(계절조정)",
      "전국 미분양주택(901Y074; 준공후 별도 원자료 후속)"
    ],
    acquisition: "api",
    auth: "api-key",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "FREM에는 ECOS_API_KEY 등 provider credential을 설정하지 않습니다. 수집·원본·정규화는 SDP가 소유하며 FREM은 SHARED_DATA_READ_TOKEN(+필요 시 Access service token)으로 중앙 dataset health/freshness를 읽기 전용 확인합니다."
  },
  {
    id: "bok-ecos-housing-price",
    layer: "real_estate",
    provider: "한국은행 ECOS · 한국부동산원 원계열",
    dataset: "StatisticTableList/StatisticItemList runtime exact discovery → official revision-vintage overlap chain → StatisticSearch",
    targetSeries: ["전국 아파트 매매가격지수 MAX", "수도권 아파트 매매가격지수 MAX", "서울 아파트 매매가격지수 MAX", "금리→공식 주택가격 직접관계"],
    acquisition: "api",
    auth: "api-key",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "FREM provider key는 없습니다. 공식 주택가격 계열의 upstream 수집·vintage 관리는 SDP가 소유하며 FREM은 중앙 dataset을 읽어 benchmark/분석에 사용합니다. 최신 관측이 stale이면 FREM에서 ECOS를 직접 호출하지 않고 SDP freshness를 점검합니다."
  },
  {
    id: "bok-ecos-h1b-legacy-mortgage",
    layer: "real_estate/credit",
    provider: "한국은행 ECOS",
    dataset: "H1B verified runtime: 121Y015/BECBLB020202 잔액금리 + 121Y011/LR20000 변동금리 stock 비중 + 511Y002/FMFB·99988 주택가격전망CSI + 511Y003 FMB/FMC/FMD 기대인플레이션 1Y/3Y/5Y; Gate C 동일모집단 총 주담대 잔액은 공식 exact 계열 미확인으로 N/A",
    targetSeries: [
      "Gate A 예금은행 주담대 잔액금리 — 연결/실데이터 확인",
      "Gate B 기존 주담대 stock 변동금리 노출비중 — 연결/실데이터 확인",
      "Gate C 동일 모집단 총 주담대 잔액 — 공식 exact 계열 미확인, 의도적 N/A",
      "Gate D 주택가격전망 CSI — 연결/실데이터 확인",
      "Gate E 기대인플레이션 1Y/3Y/5Y — 모두 연결, runtime 비교/선택"
    ],
    acquisition: "api",
    auth: "api-key",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "FREM provider key는 없습니다. A/B/D/E는 SDP 중앙 계열을 read-only로 소비합니다. Gate C는 연결 오류가 아니라 동일 모집단의 공식 총잔액 계열이 없어 N/A를 유지하고, 더 넓은 주택관련대출·차주당 평균·비중 계열로 대체하지 않습니다."
  },
  {
    id: "bok-ecos-forward-pit-snapshot",
    layer: "analysis/provenance",
    provider: "한국은행 ECOS · FREM GitHub snapshot ledger",
    dataset: "기존 ECOS 월간계열 + 공식 주택가격 앱-가시 revision change-only archive",
    targetSeries: ["2026-09 이후 월간 ECOS revision snapshot", "공식 주택가격 revision snapshot", "향후 pseudo-real-time/PIT replay 입력"],
    acquisition: "api",
    auth: "api-key",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "requires-verification",
    userAction: "과거 PIT/vintage evidence는 중앙 이관본을 read-only로 사용할 수 있지만 FREM-local snapshot workflow와 ECOS_API_KEY 경로는 retired입니다. 신규 forward-vintage 자동 축적은 SDP 쪽 검증된 revision/vintage 경로가 확인될 때만 다시 활성화하며, 그 전에는 구현 완료로 표시하지 않습니다."
  },
  {
    id: "us-treasury",
    layer: "market",
    provider: "U.S. Department of the Treasury",
    dataset: "Daily Treasury Par Yield Curve Rates",
    targetSeries: ["UST 1Y/2Y/3Y/5Y/10Y", "KR-US 2Y/3Y spread 계산 입력"],
    acquisition: "download",
    auth: "none",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "FREM 직접 Treasury 다운로드는 없습니다. SDP `ustreasury.yield-curve`를 read-only로 사용하며 중앙에 없는 2020~2023 구간은 명시적 gap으로 유지하고 FREM에서 보간·직접 재수집하지 않습니다."
  },
  {
    id: "nyfed-target-range",
    layer: "policy",
    provider: "Federal Reserve Bank of New York",
    dataset: "EFFR reference-rate records · targetRateFrom / targetRateTo",
    targetSeries: ["Fed 목표금리 하단/상단", "목표범위 중간값", "BOK-Fed 정책금리차"],
    acquisition: "api",
    auth: "none",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "FREM 직접 NY Fed 호출은 없습니다. SDP 중앙 계열을 read-only로 사용하며 목표범위 중간값은 FREM 비교용 산술 파생값이고 EFFR 자체가 아닙니다."
  },
  {
    id: "kofia-bank-bond",
    layer: "funding",
    provider: "금융투자협회 채권정보센터",
    dataset: "금융채Ⅰ(은행채) 무보증 AAA 1/3/5Y 민평",
    targetSeries: ["은행채 AAA 1Y/3Y/5Y"],
    acquisition: "crawler",
    auth: "unknown",
    collectionOwner: "UNASSIGNED",
    fremTransport: "NOT_CONNECTED",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "optional",
    userAction: "핵심 의존성에서 제외. 안정적 공식 API가 생기거나 일별 은행채 자체가 꼭 필요할 때만 추가."
  },
  {
    id: "fsc-housing-policy-regimes",
    layer: "real_estate",
    provider: "금융위원회",
    dataset: "2025-06-27 가계부채 관리 강화 / 2025-10-15 가격대별 주담대 한도 / 2026-08-13 재확인",
    targetSeries: ["주택가격대별 주담대 절대한도", "규제지역/비규제지역 LTV", "DSR 규제비율", "Housing policy regime"],
    acquisition: "manual-review",
    auth: "none",
    collectionOwner: "FREM_LOCAL_MANUAL",
    fremTransport: "PROJECT_LOCAL_STATIC",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "implemented",
    userAction: "확정 숫자 레짐은 append-only로 유지. 공식 Auto-Watcher가 새 정책 후보를 먼저 수집하되, exact LTV/DSR/한도·시행일이 검증되지 않은 후보는 기존 레짐을 자동 덮어쓰지 않음."
  },
  {
    id: "official-policy-auto-watcher",
    layer: "real_estate/policy",
    provider: "금융위원회 / 국토교통부 / 한국은행",
    dataset: "신규: FSC RSS + MOLIT RSS/공식 fallback + BOK RSS; 역사: FSC 공식 보도자료 + 대한민국 정책브리핑 국토부 공식 재배포 아카이브 역방향 backfill",
    targetSeries: ["주택금융 정책 후보 이벤트 MAX", "대정책/소정책 후보", "시행일 후보", "구조변화 시점 주변 정책 매칭"],
    acquisition: "download",
    auth: "none",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "FREM direct FSC/MOLIT/BOK crawler를 재도입하지 않습니다. SDP policy watcher의 source health/freshness를 read-only로 확인하며 후보는 unverified/autoPromotable=false로 격리해 확정 숫자 레짐이나 정책 인과판정을 변경하지 않습니다."
  },
  {
    id: "molit-rtms-housing-transactions",
    layer: "real_estate",
    provider: "국토교통부 실거래가",
    dataset: "아파트 매매 실거래 원자료",
    targetSeries: ["가격대별 거래건수", "거래비중", "중앙거래가격", "법정동×단지×전용면적 5㎡ fixed-cell 가격변화", "동일단지·면적 가격 proxy"],
    acquisition: "api",
    auth: "api-key",
    collectionOwner: "SDP",
    fremTransport: "SDP_CONSUMER_API",
    providerCredentialInFrem: "FORBIDDEN",
    status: "implemented",
    userAction: "수집과 원본 보존은 SDP의 Worker-native `rtms.apt.trade`가 담당합니다. FREM에는 provider credential·수집기·RAW가 없으며, 이관된 2006-01→2026-09 스냅샷과 그 이후 SDP canonical 파티션을 읽어 분석합니다. 새 월은 수도권 전체 지역 파티션이 완성된 경우에만 병합하고 불완전 월은 스냅샷을 유지합니다. fixed-cell은 FREM 분석 파생 단계에 남되, 중앙 인접 완결월 기반 파생 검증 전에는 신규 월에서 fail-closed합니다. 과거 SGG 생성일 공란 71건과 여주군 폐지일 1건의 successor-date operational inference 때문에 formal temporal MAX certificate는 false입니다."
  },
  {
    id: "real-estate-h5-h6-expansion",
    layer: "real_estate",
    provider: "국토교통부/한국부동산원/금융위원회",
    dataset: "H5/H6 후속 확장: 준공후 미분양·공식 가격지수 추가 지역/유형·착공/인허가/입주·PF",
    targetSeries: ["준공후 미분양", "공식 가격지수 추가 지역/유형", "착공·인허가·입주", "PF 연체/건전성"],
    acquisition: "api",
    auth: "unknown",
    collectionOwner: "UNASSIGNED",
    fremTransport: "NOT_CONNECTED",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "requires-verification",
    userAction: "전국/수도권/서울 아파트 공식 매매가격지수는 별도 ECOS runtime exact discovery로 연결 완료. 이 행은 준공후 미분양·추가 지역/유형·착공/인허가/입주·PF처럼 아직 production 연결하지 않은 확장 데이터만 뜻함."
  },
  {
    id: "housing-listing-inventory-gap",
    layer: "real_estate/market_state",
    provider: "미확정 — 공식 또는 라이선스 가능한 listing source 검증 필요",
    dataset: "기존주택 active listing inventory / 신규등록 / 철회 / DOM",
    targetSeries: ["매물 재고", "신규 매물", "매물 철회", "시장 체류기간(DOM)", "Price × Volume × Inventory 상태"],
    acquisition: "api",
    auth: "unknown",
    collectionOwner: "UNASSIGNED",
    fremTransport: "NOT_CONNECTED",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "blocked",
    userAction: "현재 ECOS·RTMS로 대체하지 않음. 공식/라이선스 소스와 이용조건이 확인될 때만 연결."
  },
  {
    id: "housing-buyer-financing-gap",
    layer: "real_estate/credit",
    provider: "미확정 — 거래와 실제 자금조달을 연결할 검증 소스 필요",
    dataset: "거래별 대출사용액 / 실제 LTV·DSR / 현금매수 여부",
    targetSeries: ["현금매수 비중", "실제 LTV", "실제 DSR", "차주구성 변화"],
    acquisition: "api",
    auth: "unknown",
    collectionOwner: "UNASSIGNED",
    fremTransport: "NOT_CONNECTED",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "blocked",
    userAction: "공개 RTMS 거래가격에서 자금조달을 추정하지 않음. 별도 공식/허가 데이터가 확보될 때만 활성화."
  },
  {
    id: "housing-forced-sale-gap",
    layer: "real_estate/credit",
    provider: "미확정 — 법원/금융기관 공식자료 검증 필요",
    dataset: "주택 경매 신규건수 / 낙찰률 / 낙찰가율 + 주담대 연체",
    targetSeries: ["강제매도 압력", "경매 유동성", "연체→매각→가격 경로"],
    acquisition: "api",
    auth: "unknown",
    collectionOwner: "UNASSIGNED",
    fremTransport: "NOT_CONNECTED",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "blocked",
    userAction: "경매·연체를 거래량 증가만으로 추정하지 않음. 공식 자료의 API/다운로드 조건을 검증한 뒤 별도 연결."
  },
  {
    id: "monetary-policy-surprise-external",
    layer: "policy/analysis",
    provider: "외부 검증 surprise series — 미연결",
    dataset: "금통위 결정 직전 기대 대비 surprise 또는 검증된 고빈도 시장 shock",
    targetSeries: ["외생적 통화정책 surprise", "causal-ready identified shock LP"],
    acquisition: "manual-review",
    auth: "unknown",
    collectionOwner: "UNASSIGNED",
    fremTransport: "NOT_CONNECTED",
    providerCredentialInFrem: "NOT_APPLICABLE",
    status: "ready-schema",
    userAction: "수집원 검증 후 코드의 external shock registry에 provider/sourceUrl/method/availableAt를 포함해 등록. 현재 KTB1Y 당일변화는 event_proxy_only로 유지."
  }

];
